import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Paper,
  ListSubheader,
  TextField,
  Box,
  Button,
} from "@mui/material";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import authAxios from "setups/axios";
import { Toaster } from "react-hot-toast";
import SyncIcon from "@mui/icons-material/Sync";

const initialFormData = {
  customer_group: { code: "" },
  customer_pricing_procedure: { code: "" },
  terms_of_payment: { code: "" },
  incoterms: { code: "" },
  account_assignment_group: { code: "" },
  reconciliation_account: { code: "" },
  account_group: { code: "" },
  sales_office: { code: "" },
  distribution_channel: { code: "" },
  division: { code: "" },
  state: { code: "" },
  customer_class: { code: "" },
  status: { code: "" },
  price_group: { code: "" },
  price_list: { code: "" },
  region: { code: "" },
  customer_segment: { code: "" },
  credit_limit: { code: "" },
  payment_terms: { code: "" },
  delivery_priority: { code: "" },
  tax_classification: { code: "" },
  language: { code: "" },
  sales_district: { code: "" },
  sales_group: { code: "" },
  price_list_group: { code: "" },
  incoterm_location: { code: "" },
  billing_block: { code: "" },
  shipping_condition: { code: "" },
  customer_status: { code: "" },
  account_manager: { code: "" },
  sales_channel: { code: "" },
  distribution_method: { code: "" },
  gst_number: { code: "" },
  ninnumber: { code: "" },
  latitude: { code: "" },
  longitude: { code: "" },
  city: { code: "" },
  postal_code: { code: "" },
  country: { code: "" },
  contact_person: { code: "" },
  phone_number: { code: "" },
  email: { code: "" },
  address: { code: "" },
};

const brandData = {
  "Tyre-Group-1": ["SW", "AW"],
  "Tyre-Group-2": ["12", "13"],
};

const SelectField = ({ label, fieldKey, options, value, onChange }) => (
  <FormControl fullWidth margin="normal" size="small">
    <InputLabel>{label}</InputLabel>
    <Select
      value={value}
      label={label}
      onChange={(e) => onChange(fieldKey, e.target.value)}
    >
      {options?.map((item) => (
        <MenuItem key={item.code} value={item.code}>
          {item.description || item.name} ({item.code})
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const TextFieldInput = ({ label, fieldKey, value, onChange, type = "text" }) => (
  <TextField
    fullWidth
    margin="normal"
    size="small"
    label={label}
    type={type}
    value={value}
    onChange={(e) => onChange(fieldKey, e.target.value)}
  />
);

const TyreBrandSelector = ({ selectedTyreGroups, onSelect }) => {
  return (
    <FormControl fullWidth margin="normal" size="small">
      <InputLabel>Tyre Brand</InputLabel>
      <Select
        value=""
        onChange={(e) => {
          const [group, value] = e.target.value.split("::");
          onSelect(group, value);
        }}
        displayEmpty
      >
        <ListSubheader>Tyre-Group-1</ListSubheader>
        {brandData["Tyre-Group-1"].map((item) => (
          <MenuItem key={item} value={`Tyre-Group-1::${item}`}>
            {item}
          </MenuItem>
        ))}

        <ListSubheader>Tyre-Group-2</ListSubheader>
        {brandData["Tyre-Group-2"].map((item) => (
          <MenuItem key={item} value={`Tyre-Group-2::${item}`}>
            {item}
          </MenuItem>
        ))}
      </Select>

      <Typography variant="body2" mt={2}>
        Group 1 Selected: {selectedTyreGroups["Tyre-Group-1"] || "None"}
        <br />
        Group 2 Selected: {selectedTyreGroups["Tyre-Group-2"] || "None"}
      </Typography>
    </FormControl>
  );
};

const Main = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [masterData, setMasterData] = useState({});
  const [selectedTyreGroups, setSelectedTyreGroups] = useState({
    "Tyre-Group-1": "",
    "Tyre-Group-2": "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await authAxios()({
          url: `/api/v1/actyv/onboarding`,
          method: "GET",
        });
        if (response?.data?.status === "success") {
          setMasterData(response.data.data);
        }
      } catch (error) {
        // handle error if needed
      }
    };
    fetchData();
  }, []);

  const handleSelectChange = (field, value) => {
    const selected = masterData[field]?.find((opt) => opt.code === value);
    setFormData((prev) => ({
      ...prev,
      [field]: selected || { code: value },
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: { code: value },
    }));
  };

  const handleSelectTyreBrand = (group, value) => {
    setSelectedTyreGroups((prev) => ({
      ...prev,
      [group]: value,
    }));
  };

  const handleAddNotification = async () => {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      new Notification("Hello!", {
        body: "You enabled notifications! 🎉",
      });
    }
  };

  const formFields = [
    { label: "Customer Group", key: "customer_group", type: "select" },
    { label: "Customer Pricing", key: "customer_pricing_procedure", type: "select" },
    { label: "Terms of Payment", key: "terms_of_payment", type: "select" },
    { label: "Incoterms", key: "incoterms", type: "select" },
    { label: "Account Assignment Group", key: "account_assignment_group", type: "select" },
    { label: "Reconciliation Account", key: "reconciliation_account", type: "select" },
    { label: "Account Group", key: "account_group", type: "select" },
    { label: "Sales Office", key: "sales_office", type: "select" },
    { label: "Distribution Channel", key: "distribution_channel", type: "select" },
    { label: "Division", key: "division", type: "select" },
    { label: "State", key: "state", type: "select" },
    { label: "Customer Class", key: "customer_class", type: "select" },
    { label: "Status", key: "status", type: "select" },
    { label: "Price Group", key: "price_group", type: "select" },
    { label: "Price List", key: "price_list", type: "select" },
    { label: "Region", key: "region", type: "select" },
    { label: "Customer Segment", key: "customer_segment", type: "select" },
    { label: "Payment Terms", key: "payment_terms", type: "select" },
    { label: "Delivery Priority", key: "delivery_priority", type: "select" },
    { label: "Tax Classification", key: "tax_classification", type: "select" },
    { label: "Language", key: "language", type: "select" },
    { label: "Sales District", key: "sales_district", type: "select" },
    { label: "Sales Group", key: "sales_group", type: "select" },
    { label: "Price List Group", key: "price_list_group", type: "select" },
    { label: "Incoterm Location", key: "incoterm_location", type: "select" },
    { label: "Billing Block", key: "billing_block", type: "select" },
    { label: "Shipping Condition", key: "shipping_condition", type: "select" },
    { label: "Customer Status", key: "customer_status", type: "select" },
    { label: "Account Manager", key: "account_manager", type: "select" },
    { label: "Sales Channel", key: "sales_channel", type: "select" },
    { label: "Distribution Method", key: "distribution_method", type: "select" },
    { label: "GST Number", key: "gst_number", type: "text" },
    { label: "NIN Number", key: "ninnumber", type: "text" },
    { label: "Latitude", key: "latitude", type: "number" },
    { label: "Longitude", key: "longitude", type: "number" },
    { label: "City", key: "city", type: "text" },
    { label: "Postal Code", key: "postal_code", type: "text" },
    { label: "Country", key: "country", type: "text" },
    { label: "Contact Person", key: "contact_person", type: "text" },
    { label: "Phone Number", key: "phone_number", type: "text" },
    { label: "Email", key: "email", type: "email" },
    { label: "Address", key: "address", type: "text" },
    { label: "Credit Limit", key: "credit_limit", type: "number" },
  ];

  const handleSubmit = () => {
    console.log("Submit Data:", formData);
  };

  return (
    <>
      <Box p={2} mt={6} display="flex" justifyContent="center">
        <Paper
          elevation={3}
          sx={{ padding: 3, border: "1px solid #ccc", width: "100%", maxWidth: 1200 }}
        >
          <Typography variant="h6" gutterBottom>
            Dealer Onboarding Form
          </Typography>

          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))"
            gap={2}
            maxHeight="75vh"
            sx={{ overflowY: "auto", pr: 1 }}
          >
            {formFields.map(({ label, key, type }) =>
              type === "select" ? (
                <SelectField
                  key={key}
                  label={label}
                  fieldKey={key}
                  options={masterData[key]}
                  value={formData[key]?.code || ""}
                  onChange={handleSelectChange}
                />
              ) : (
                <TextFieldInput
                  key={key}
                  label={label}
                  fieldKey={key}
                  value={formData[key]?.code || ""}
                  onChange={handleInputChange}
                  type={type}
                />
              )
            )}
          </Box>

          <Box mt={2}>
            <TyreBrandSelector
              selectedTyreGroups={selectedTyreGroups}
              onSelect={handleSelectTyreBrand}
            />
          </Box>

          <Box mt={4} display="flex" justifyContent="space-between">
            <Button onClick={handleAddNotification} variant="outlined" color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Submit
            </Button>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

const Onboard = () => {
  return (
    <AccessController roles={["AO", "RO", "ZO", "ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default Onboard;
